import { GDM } from "../helpers/datamanager";
import { DataTable } from "../loaders/stb";
import { LookupTable } from "../loaders/ltb";
import { StringTable } from "../loaders/stl";
import { AnimationData } from "../loaders/zmo";
import { QuestScriptManager } from "../events/questscriptmanager";
import { ItemDataManager } from "../datamanagers/itemdatamanager";
import { SkillDataManager } from "../datamanagers/skilldatamanager";
import { MorphAnimManager } from "../datamanagers/morphanimmanager";
import { ModelListManager } from "../datamanagers/modellistmanager";
import { registerCharacterData } from "../registrations";
import type { GameContext, GameMiddleware } from "./types";

/**
 * Registers all ROSE Online data tables with the global DataManager (GDM).
 *
 * Replaces the duplicate GDM.register() calls that previously existed in
 * both core.ts (load event) and rose-editor.ts (registerDataManagers()).
 *
 * GDM uses lazy loading — registering a table never triggers a network
 * request until something calls GDM.get(key).
 *
 * Registration is done in the constructor (not init) so that tables are
 * available as soon as `new RoseOnlineMiddleware()` is constructed —
 * before game.init() runs — allowing editor's onInit() to safely call
 * GDM.get() (e.g. via createCharacter → EditorCharacter constructor).
 */
export class DataMiddleware implements GameMiddleware {
    constructor() {
        // Zone / world data (shared: editor needs these for map loading)
        GDM.register("list_zone", DataTable, "3DDATA/STB/LIST_ZONE.STB");
        GDM.register("zone_names", StringTable, "3DDATA/STB/LIST_ZONE_S.STL");
        GDM.register("list_sky", DataTable, "3DDATA/STB/LIST_SKY.STB");

        // Character / NPC data (shared: editor preview shows characters)
        registerCharacterData();

        // Morph animations (shared)
        GDM.register(
            "morph_anims",
            MorphAnimManager,
            "3DDATA/STB/LIST_MORPH_OBJECT.STB",
        );

        // Effects & events
        GDM.register("file_effect", DataTable, "3DDATA/STB/FILE_EFFECT.STB");
        GDM.register("list_effect", DataTable, "3DDATA/STB/LIST_EFFECT.STB");
        GDM.register("file_sound", DataTable, "3DDATA/STB/FILE_SOUND.STB");
        GDM.register("list_hitsound", DataTable, "3DDATA/STB/LIST_HITSOUND.STB");
        GDM.register("list_event", DataTable, "3DDATA/STB/LIST_EVENT.STB");
        GDM.register("con_strings", LookupTable, "3DDATA/EVENT/ULNGTB_CON.LTB");
        GDM.register(
            "quest_scripts",
            QuestScriptManager,
            "3DDATA/STB/LIST_QUESTDATA.STB",
        );

        // Item & skill data
        GDM.register("item_data", ItemDataManager);
        GDM.register("skill_data", SkillDataManager);
        GDM.register("list_class", DataTable, "3DDATA/STB/LIST_CLASS.STB");
        GDM.register("list_status", DataTable, "3DDATA/STB/LIST_STATUS.STB");

        // Quest data
        GDM.register("list_quest", DataTable, "3DDATA/STB/LIST_QUEST.STB");
        GDM.register("quest_names", StringTable, "3DDATA/STB/LIST_QUEST_S.STL");

        // Field items
        GDM.register(
            "fielditem_models",
            ModelListManager,
            "3DDATA/ITEM/LIST_FIELDITEM.ZSC",
        );
        GDM.register("fielditem_ani", AnimationData, "3DDATA/MOTION/ITEM_ANI.ZMO");
    }

    init(_ctx: GameContext): void {}
}
